import { CustomError } from '@app/models/general';
import { useAppDispatch, useAppSelector } from '@app/redux/hooks';
import {
  register,
  registerWithGoogle,
  validateEmailActivation,
  registerInviteUser,
} from '@app/services/auth';
import { getCategories, verifyBusinessEmail } from '@app/services/business';
import { registrationValidation } from '@app/utils/validations';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, useRef, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { SubscriptionTypeValue } from '@app/utils/consts.tsx';

export const useSignUpPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const businessState = useAppSelector((state) => state.business);
  const [signupError, setSignupError] = useState<string | undefined>();
  const [emailAlreadyExists, setEmailAlreadyExists] = useState(false);
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [businessEmailAlreadyExists, setBusinessEmailAlreadyExists] =
    useState(false);
  const [businessEmailError, setBusinessEmailError] = useState<
    string | undefined
  >();

  const signupForm = useForm({
    resolver: registrationValidation,
    defaultValues: {
      fullName: '',
      email: '',
      password: '',
      confirmPassword: '',
      businessName: '',
      businessEmail: '',
      businessAddress: '',
      businessCategory: '',
      teamSize: '',
      terms: false,
    },
  });

  const { data: businessCategories = [], isLoading } = useQuery({
    queryKey: ['businessCategories'],
    queryFn: () => getCategories(),
  });

  const { mutateAsync: validateEmail, isPending: isValidatingEmail } =
    useMutation({
      mutationKey: ['validateEmail'],
      mutationFn: (email: string) => validateEmailActivation(email),
    });
  const {
    mutateAsync: validateBusinessEmail,
    isPending: isValidatingBusinessEmail,
  } = useMutation({
    mutationKey: ['validate-business-email'],
    mutationFn: (email: string) => verifyBusinessEmail(email),
  });


  const { mutate: mutateInviteSignup, isPending: isInviteSignupPending } =
    useMutation({
      mutationKey: ['register-invite'],
      mutationFn: (data: {
        firstName: string;
        lastName: string;
        email: string;
        password: string;
        phoneNumber?: string;
        inviteToken?: string;
      }) => registerInviteUser(data),
      onSuccess: (res) => {
        dispatch({ type: 'auth/save', payload: res?.data?.user });
        localStorage.setItem('accessToken', res?.data?.accessToken);

        const pendingInviteToken = localStorage.getItem('pendingInviteToken');
        if (pendingInviteToken) {
          localStorage.removeItem('pendingInviteEmail');
          localStorage.removeItem('signupFormData');
          navigate(`/accept-invite/${pendingInviteToken}`, { replace: true });
        }
      },
      onError: (err: CustomError) => {
        console.error(err);
        setSignupError(
          err?.data?.error?.message ?? 'Error while creating account'
        );
      },
    });

  const { mutate, isPending } = useMutation({
    mutationKey: ['register'],
    mutationFn: (data: {
      firstName: string;
      lastName: string;
      email: string;
      password: string;
      business: {
        name: string;
        email: string;
        address: string;
        category: string;
        planName?: string;
      };
      phoneNumber?: string;
      isTrial?: boolean;
      planType?: SubscriptionTypeValue;
    }) => register(data),
    onSuccess: (res) => {
      dispatch({ type: 'auth/save', payload: res?.data?.user });
      dispatch({ type: 'business/loadList', payload: res?.data?.businesses });
      dispatch({
        type: 'business/selectBusiness',
        payload: { id: res?.data?.businesses?.[0]?.id },
      });
      localStorage.setItem('accessToken', res?.data?.accessToken);

      // Save Google access token if present
      if (res?.data?.googleAccessToken) {
        localStorage.setItem('googleAccessToken', res?.data?.googleAccessToken);
      }

      // console.log(res.data);

      // // Navigate to trial signup confirmation page after successful account creation
      // setTimeout(() => {
      //   navigate('/trial-sign-up-confirmation');
      // }, 1000);

      // Navigate to free or trial signup confirmation page after successful account creation
      setTimeout(() => {
        navigate(res?.data?.user?.isTrial ? '/trial-sign-up-confirmation' : '/free-sign-up-confirmation');
      }, 1000);
    },
    onError: (err: CustomError) => {
      console.error(err);
      setSignupError(err?.data?.error?.message ?? 'Error while creating account');
    },
  });

  const handleBusinessEmailValidation = async (
    email: string
  ): Promise<boolean> => {
    setBusinessEmailAlreadyExists(false);
    setBusinessEmailError(undefined);

    try {
      await validateBusinessEmail(email);
      return true;
    } catch (err: any) {
        setBusinessEmailAlreadyExists(true);
        setBusinessEmailError('Business email already exists');
        return false;
    }
  };

  const clearBusinessEmailValidationError = () => {
    setBusinessEmailAlreadyExists(false);
    setBusinessEmailError(undefined);
  };


  const submitSignup = async (values: FieldValues) => {
    setSignupError(undefined);

    // Split full name into first and last name
    const fullName = values.fullName || '';
    const nameParts = fullName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    // Validate password confirmation
    if (values.password !== values.confirmPassword) {
      setSignupError('Passwords do not match');
      return;
    }

    // Validate terms acceptance
    if (!values.terms) {
      setSignupError('Please accept the Terms of Service and Privacy Policy');
      return;
    }

    // Store form data in localStorage for the confirmation page
    localStorage.setItem('signupFormData', JSON.stringify(values));

    mutate({
      firstName,
      lastName,
      email: values.email,
      password: values.password,
      phoneNumber: values.phoneNumber || '',
      business: {
        name: values.businessName,
        email: values.businessEmail || values.email,
        address: values.businessAddress,
        category: values.businessCategory,
        planName: values.planName,
      },
      isTrial: values.isTrial || false,
      planType: values.planName,
    });
  };

  const submitInviteSignup = async (values: FieldValues) => {
    setSignupError(undefined);

    // Split full name into first and last name
    const fullName = values.fullName || '';
    const nameParts = fullName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    // Validate password confirmation
    if (values.password !== values.confirmPassword) {
      setSignupError('Passwords do not match');
      return;
    }

    // Validate terms acceptance
    if (!values.terms) {
      setSignupError('Please accept the Terms of Service and Privacy Policy');
      return;
    }

    mutateInviteSignup({
      firstName,
      lastName,
      email: values.email,
      password: values.password,
      phoneNumber: values.phoneNumber || '',
      inviteToken: values.inviteToken,
    });
  };

  const submitGoogleSignup = () => {
    registerWithGoogle()
      .then((res: any) => {
        if (res?.data) {
          // Store the Google access token
          if (res?.data?.accessToken) {
            localStorage.setItem('googleAccessToken', res.data.accessToken);
          }
          if (res?.data?.userName) {
            signupForm.setValue('fullName', res.data.userName);
          }
          if (res?.data?.email) {
            signupForm.setValue('email', res.data.email);
          }
        }
      })
      .catch((err) => {
        console.error(err);
        setSignupError('Error while signing up with Google');
      });
  };

  const handleEmailValidation = async (email: string): Promise<boolean> => {
    try {
      await validateEmail(email);
      // Validation passed
      return true;
    } catch (error: any) {
      if (error?.status === 400) {
        setEmailAlreadyExists(true);

        redirectTimeoutRef.current = setTimeout(() => {
          navigate('/login');
        }, 3000);

        return false;
      }
      return true;
    }
  };

  const clearEmailValidationError = () => {
    setEmailAlreadyExists(false);
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
      redirectTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  return {
    signupForm,
    submitSignup,
    submitInviteSignup,
    signupError,
    submitGoogleSignup,
    businessState,
    isPending,
    isInviteSignupPending,
    isLoading,
    businessCategories,
    handleEmailValidation,
    isValidatingEmail,
    emailAlreadyExists,
    clearEmailValidationError,
    handleBusinessEmailValidation,
    isValidatingBusinessEmail,
    businessEmailAlreadyExists,
    businessEmailError,
    clearBusinessEmailValidationError,
  };
};
